// src/pages/configuracion/components/UsuarioFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Switch,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { toast } from '@/components/ui/Toast';
import { Save } from 'lucide-react';
import { useCrearUsuario, useActualizarUsuario, useRoles } from '@/hooks/queries/useConfiguracion';
import { useClientes } from '@/hooks/queries/useClientes';
import type { UsuarioAdmin } from '@/types';

const createSchema = z.object({
  email: z.string().email('Email inválido'),
  nombre: z.string().min(1, 'El nombre es requerido'),
  apellido: z.string().optional(),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
  tipo: z.enum(['INTERNO', 'CLIENTE']),
  roles: z.array(z.string()).min(1, 'Seleccione al menos un rol'),
  cliente_id: z.string().optional(),
  activo: z.boolean(),
});

const updateSchema = createSchema.omit({ password: true }).extend({
  password: z.string().optional(),
});

type CreateFormData = z.infer<typeof createSchema>;
type UpdateFormData = z.infer<typeof updateSchema>;

interface UsuarioFormModalProps {
  open: boolean;
  onClose: () => void;
  usuario: UsuarioAdmin | null;
}

export function UsuarioFormModal({ open, onClose, usuario }: UsuarioFormModalProps) {
  const isEditing = !!usuario;
  const { data: roles } = useRoles();
  const { data: clientesData } = useClientes();
  const crearMutation = useCrearUsuario();
  const actualizarMutation = useActualizarUsuario();

  const form = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEditing ? updateSchema : createSchema),
    defaultValues: {
      email: '',
      nombre: '',
      apellido: '',
      password: '',
      tipo: 'INTERNO',
      roles: [],
      cliente_id: '',
      activo: true,
    },
  });

  const tipoValue = form.watch('tipo');

  useEffect(() => {
    if (usuario) {
      form.reset({
        email: usuario.email,
        nombre: usuario.nombre,
        apellido: usuario.apellido ?? '',
        password: '',
        tipo: usuario.tipo,
        roles: usuario.roles.map((r) => r.id),
        cliente_id: usuario.cliente_id ?? '',
        activo: usuario.activo,
      });
    } else {
      form.reset({
        email: '',
        nombre: '',
        apellido: '',
        password: '',
        tipo: 'INTERNO',
        roles: [],
        cliente_id: '',
        activo: true,
      });
    }
  }, [usuario, form]);

  const onSubmit = async (data: CreateFormData | UpdateFormData) => {
    try {
      if (isEditing && usuario) {
        await actualizarMutation.mutateAsync({
          id: usuario.id,
          data: {
            nombre: data.nombre,
            apellido: data.apellido,
            tipo: data.tipo,
            roles: data.roles,
            cliente_id: data.tipo === 'CLIENTE' ? data.cliente_id : undefined,
            activo: data.activo,
          },
        });
        toast.success('Usuario actualizado correctamente');
      } else {
        await crearMutation.mutateAsync(data as CreateFormData);
        toast.success('Usuario creado correctamente');
      }
      onClose();
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const rolesOptions = roles?.map((r) => ({
    value: r.id,
    label: r.nombre || r.codigo,
  })) ?? [];

  const clientesOptions = clientesData?.items?.map((c: { id: string; nombre: string }) => ({
    value: c.id,
    label: c.nombre,
  })) ?? [];

  const selectedRoles = form.watch('roles') || [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nombre"
              {...form.register('nombre')}
              error={form.formState.errors.nombre?.message}
            />
            <Input
              label="Apellido"
              {...form.register('apellido')}
            />
          </div>

          <Input
            label="Email"
            type="email"
            {...form.register('email')}
            error={form.formState.errors.email?.message}
            disabled={isEditing}
          />

          {!isEditing && (
            <Input
              label="Contraseña"
              type="password"
              {...form.register('password')}
              error={(form.formState.errors as any).password?.message}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de usuario</label>
              <Select
                value={form.watch('tipo')}
                onValueChange={(value) => form.setValue('tipo', value as 'INTERNO' | 'CLIENTE')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNO">Interno</SelectItem>
                  <SelectItem value="CLIENTE">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tipoValue === 'CLIENTE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Cliente</label>
                <Select
                  value={form.watch('cliente_id') ?? ''}
                  onValueChange={(value) => form.setValue('cliente_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clientesOptions.map((c: { value: string; label: string }) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
              {rolesOptions.map((rol) => {
                const isSelected = selectedRoles.includes(rol.value);
                return (
                  <button
                    key={rol.value}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        form.setValue('roles', selectedRoles.filter((r: string) => r !== rol.value));
                      } else {
                        form.setValue('roles', [...selectedRoles, rol.value]);
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {rol.label}
                  </button>
                );
              })}
            </div>
            {form.formState.errors.roles && (
              <p className="text-sm text-red-600 mt-1">{form.formState.errors.roles.message}</p>
            )}
          </div>

          {isEditing && (
            <div className="flex items-center justify-between py-2 border-t">
              <div>
                <label className="font-medium text-gray-900">Usuario activo</label>
                <p className="text-sm text-gray-500">Los usuarios inactivos no pueden iniciar sesion</p>
              </div>
              <Switch
                checked={form.watch('activo')}
                onCheckedChange={(checked) => form.setValue('activo', checked)}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={crearMutation.isPending || actualizarMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
